"use client";

import { ChevronLeftIcon } from "lucide-react";

/**
 * Chrome shared by every form step in the register flow: the Back control and
 * the progress bar above the content.
 *
 * The design's progress fill is 155.5px of a 1244px track — exactly one eighth
 * — so the flow is laid out as 8 steps and each screen passes its own index.
 */
export function StepShell({
  step,
  totalSteps,
  onBack,
  children,
}: {
  step: number;
  totalSteps: number;
  onBack: () => void;
  children: React.ReactNode;
}) {
  const percent = Math.min(100, Math.max(0, (step / totalSteps) * 100));

  return (
    <div className="w-full">
      {/* Sticky under the site nav, which is `sticky top-0` at min-h-16 /
          xl:min-h-20 — hence the 4rem/5rem offsets. Its own height (~70px:
          20px of text + 12px gap + 5px bar + 32px padding) is what the
          step-level sticky elements offset themselves by, see
          FLOW_HEADER_OFFSET notes in OrderSummaryCard/PackageReadyStep. */}
      <div className="sticky top-16 z-40 w-full border-b border-white/5 bg-black/70 backdrop-blur-md xl:top-20">
        <div className="mx-auto flex w-full max-w-[1244px] flex-col gap-3 px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 inline-flex w-fit items-center gap-1 rounded font-inter text-sm font-semibold text-white transition-opacity hover:opacity-80"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back
          </button>

          <div
            className="h-[5px] w-full overflow-hidden rounded-[55px] bg-white/10"
            role="progressbar"
            aria-valuenow={step}
            aria-valuemin={0}
            aria-valuemax={totalSteps}
            aria-label={`Step ${step} of ${totalSteps}`}
          >
            <div
              className="h-full rounded-[55px] bg-[#0B3CFF] transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}
